/**
 * PR-G — Array-rect grip helper (Q12.B).
 *
 * Rectangular Associative Array (AutoCAD ARRAYRECT) is rendered as a
 * single InstancedMesh in PR-D, but its grips are intentionally split
 * across two layers:
 *
 *   - The 5 canonical AutoCAD grips (origin / col-count / row-count /
 *     col-spacing / row-spacing) come from `calculateGrips` via
 *     `computeGrips()` — same data path as every other entity.
 *
 *   - Optional row / column *insertion* chevrons live alongside the
 *     count grips so the user can spawn a new row/column without
 *     opening the Properties palette. These are a Q12.B feature
 *     ("array grips: row/col insertion chevrons + base grip + corner
 *     grip") that AutoCAD shows on hover, and they are emitted only
 *     for entities of type "Rectangular Associative Array".
 *
 * The function is pure — it takes the array's property bag and returns
 * extra Grip records that the overlay paints on top of the standard
 * `computeGrips()` output.
 *
 * The two extra grips per axis (col / row insertion chevron) live at:
 *   - col-insert : base + (columns × columnSpacing, 0)
 *   - row-insert : base + (0, rows × rowSpacing)
 *
 * Glyph: `triangle` with `gripRotationDeg` so the apex points in the
 * +column or +row direction (matching the existing array-count
 * triangles in `renderShapedGrips.tsx`).
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import {
  readNumber,
  resolveArrayAxisAngleDeg,
  rotateOffsetAroundBaseDeg,
} from "../../scene/renderHelpers";

import type { Grip } from "./computeGrips";

/**
 * Compute row / column *insertion* chevrons for a Rectangular
 * Associative Array. Returns an empty list when the entity is not an
 * array — the caller is expected to pre-filter, but the guard makes
 * the helper safe to call on any EntityPreview.
 */
export function computeArrayInsertionGrips(entity: EntityPreview): Grip[] {
  if (entity.type !== "Rectangular Associative Array") return [];

  const p = entity.properties;
  const hasBase = "Base X" in p || "Base Y" in p;
  const baseX = readNumber(p, hasBase ? "Base X" : "Origin X");
  const baseY = readNumber(p, hasBase ? "Base Y" : "Origin Y");
  const rows = Math.max(1, Math.round(readNumber(p, "Rows", 2)));
  const columns = Math.max(1, Math.round(readNumber(p, "Columns", 3)));
  const rowSpacing = readNumber(p, "Row spacing", 60);
  const columnSpacing = readNumber(p, "Column spacing", 80);
  // S-ARRAYRECT-AXISANGLE-UNIT — see renderHelpers.ts:resolveArrayAxisAngleDeg.
  // Keeps the insertion-chevron grips on the SAME rotated cell corners the
  // render now honours (otherwise they'd drift off the visibly-rotated grid).
  const axisAngleDeg = resolveArrayAxisAngleDeg(p);

  const colInsert = rotateOffsetAroundBaseDeg(
    baseX,
    baseY,
    columns * columnSpacing,
    0,
    axisAngleDeg,
  );
  const rowInsert = rotateOffsetAroundBaseDeg(
    baseX,
    baseY,
    0,
    rows * rowSpacing,
    axisAngleDeg,
  );

  return [
    {
      id: "array-col-insert",
      entityId: entity.id,
      kind: "array-column-count",
      shape: "triangle",
      world: { x: colInsert.x, y: colInsert.y },
      rotationDeg: axisAngleDeg,
      payload: { role: "column-insert", columns, rows },
    },
    {
      id: "array-row-insert",
      entityId: entity.id,
      kind: "array-row-count",
      shape: "triangle",
      world: { x: rowInsert.x, y: rowInsert.y },
      rotationDeg: axisAngleDeg + 90,
      payload: { role: "row-insert", columns, rows },
    },
  ];
}
